import React, { Component } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { TranslatorContext } from '@jutro/locale';
import {
    InfoLabel, Icon, Loader, FormattedDateRange, Link
} from '@jutro/components';
import { GatewayPolicyService, UserService } from 'gw-capability-ri-policy';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { LobIconUtil } from 'gw-portals-util-js';
import { PolicyClaims } from 'gw-capability-ri-claim-react';
import { isCapabilityEnabled, hasPermission_RI } from 'gw-portals-config-js';
import { GatewayDocumentService } from 'gw-capability-gateway-document';
import { DocumentsPage } from 'gw-capability-document-react';
import { DependencyProvider } from 'gw-portals-dependency-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { PolicyService } from 'gw-capability-policy';
import CommonRoutes from '../Common/CommonRouting';
import metadata from './RIPolicyDetails.metadata.json5';
import styles from '../Policies.module.scss';
import messages from '../Policies.messages';
import pageRouting from '../Common/Policies-config.json5';

const { capabilitiesConfig } = appConfig;

const DocumentPageService = {
    getDocuments: GatewayPolicyService.getDocumentsForPolicy,
    generateUploadToken: GatewayDocumentService.generateUploadToken,
    uploadDocument: GatewayDocumentService.uploadDocument,
    removeDocument: GatewayDocumentService.removeDocument,
    downloadDocument: GatewayDocumentService.downloadDocument
};

function documentFeatureTurnedOn(routes) {
    const documentRoute = {
        path: '/documents',
        component: DocumentsPage
    };
    if (isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'document' })) {
        routes.push(documentRoute);
    }
}

class RIPolicyDetails extends Component {
    static contextType = TranslatorContext;

    static propTypes = {
        match: PropTypes.shape({
            path: PropTypes.string,
            params: PropTypes.shape({
                policyNumber: PropTypes.string
            }),
            url: PropTypes.string
        }).isRequired,
        authHeader: PropTypes.shape({
            Authorization: PropTypes.string
        }).isRequired,
        authUserData: PropTypes.object.isRequired,
        isLoggedIn: PropTypes.bool,
        location: PropTypes.shape({
            state: PropTypes.string,
            pathname: PropTypes.string
        }).isRequired
    };

    state = {
        policyResponse: {},
        policyBillingData: {},
        currentView: undefined,
        isLoading: true,
        billingIcon: 'mi-payment'
    };

    componentDidMount() {
        this.getPolicyDetails();
    }

    componentDidUpdate(prevProps) {
        if(prevProps.match.params.policyNumber != this.props.match.params.policyNumber) {
            this.setState({
                isLoading: true
            });
            this.getPolicyDetails();
            this.props.history.push("/ri-policies/"+this.props.match.params.policyNumber+"/summary");
        } 
    }  

    getAccountHolderNameandNumber = (accountData, type) => {
        if (_.isEmpty(accountData)) {
            return null;
        }
        if (type === 'status') {
            return accountData.latestPeriod.displayStatus;
        }
        return accountData.latestPeriod.primaryInsuredName;
    };

    handlePolicNumberOnClick = () => {
        this.handleTilesOnClick('terms');
    };

    getRoutesWithFeature() {
        const routes = [...pageRouting];
        this.billingFeatureTurnedOn(routes);
        this.claimFeatureTurnedOn(routes);
        documentFeatureTurnedOn(routes);
        return routes;
    }

    billingFeatureTurnedOn = (routes) => {
        if (this.hasBillingAccess()) {
            const billingRoute = {
                path: '/billing',
                componentPath: 'Policies/Billing/Billing'
            };
            routes.push(billingRoute);
        }
    };

    claimFeatureTurnedOn = (routes) => {
        if (this.isClaimsTurnedOn()) {
            const claimRoute = {
                path: '/claims',
                component: PolicyClaims
            };
            routes.push(claimRoute);
        }
    };

    updateCountInfo = (type) => {
        const { policyResponse } = this.state;
        const currentCount = _.get(policyResponse, `numberOf${type}`);
        _.set(policyResponse, `numberOf${type}`, currentCount + 1);
        this.setState({ policyResponse });
    };

    updateDocumentCount = (count, type = 'Documents') => {
        const { policyResponse } = this.state;
        _.set(policyResponse, `numberOf${type}`, count);
        this.setState({ policyResponse });
    };

    getPolicyDetails = async () => {
        const {
            isLoggedIn,
            authHeader,
            match: {
                params: { policyNumber }
            },
            location: { state: redirectPath, pathname }
        } = this.props;

        // FB 16551    
        var defaultPolicy = policyNumber;
        if (_.isEmpty(policyNumber)) {
            await PolicyService.getAccountPolicySummaries(authHeader).then(
                (getPolicyResponse = {}) => { 
                    if (!_.isUndefined(getPolicyResponse) && !(Object.keys(getPolicyResponse).length === 0)) {
                        const routePath = pathname && (pathname.trim().split('/') || []);
                        const selectedPageFromPath = routePath[routePath.length - 1];
                        const activeTile = selectedPageFromPath || redirectPath || 'summary'; 
                        getPolicyResponse.numberOfDocuments=0;
                            defaultPolicy = _.get(getPolicyResponse[0].periods, '[0].policyId')
                            return this.props.history.push("/ri-policies/"+defaultPolicy+"/summary"); 
                        this.setState({
                            currentView: activeTile,
                        });
                    } 
                }
            );     
        } 
        
        await GatewayPolicyService.getPolicyBillingSummaryInfo(policyNumber, authHeader).then(
            (billingResponseData = {}) => {
                if (!_.isUndefined(billingResponseData.policyPeriodBillingSummaries)) {
                    const billingData = billingResponseData.policyPeriodBillingSummaries;
                    const billingIcon = _.get(billingData, '[0].isDelinquent')
                        ? 'mi-warning'
                        : 'mi-payment';
                    this.setState({
                        policyBillingData: billingResponseData,
                        billingIcon: billingIcon
                    });
                }
            }
        );

        await GatewayPolicyService.getPolicy(policyNumber, authHeader).then(
            (getPolicyResponse) => {                
                if (!_.isUndefined(getPolicyResponse)) {
                    const routePath = pathname && (pathname.trim().split('/') || []);
                    const selectedPageFromPath = routePath[routePath.length - 1];
                    const activeTile = selectedPageFromPath || redirectPath || 'summary';

                    this.setState({
                        currentView: activeTile,
                        policyResponse: getPolicyResponse
                    });
                }
            }
        );

        GatewayPolicyService.addRecentlyViewedPolicy(policyNumber, authHeader);
        this.setState({
            isLoading: false
        });
    };

    getPolicyHeader = () => {
        const {
            policyResponse,
            policyBillingData
        } = this.state;

        let policyHolderName = null;
        let policyStatus = null;
        let policyNumber = null;
        let activePeriod = null;
        let billingNumber = null;
        let productName = null;
        let billingStatus = null;

        if (!_.isEmpty(policyResponse)) {
            activePeriod = policyResponse.latestPeriod;
            policyStatus = _.get(policyResponse, 'latestPeriod.displayStatus'); //FB 22533
            policyHolderName = _.get(policyResponse, 'latestPeriod.primaryInsuredName');
            policyNumber = _.get(policyResponse, 'latestPeriod.policyNumber');
            billingNumber = _.get(policyBillingData, 'policyPeriodBillingSummaries[0].billingAccountNumber_Ext');
            productName = _.get(policyResponse, 'product.productCode');
            billingStatus = _.get(policyBillingData, 'policyPeriodBillingSummaries[0].accountStatus_Ext');
        }
        const statusClassName = billingStatus === 'Delinquent' ? styles.riInfoStatusAlert : styles.gwInfoStatus;

        /*  className={styles.gwTypeWithLOB}  className={styles.gwPageTitle} */
        return (
            <div>
                <div className={styles.riSubTitle}>
                    <div>
                        Account #: &nbsp;&nbsp;
                        <span>
                            <Link to={`/ri-billing/${policyNumber}/summary`} className={styles.riLinkDetails}>
                                {billingNumber}
                            </Link>
                        </span>
                        <InfoLabel
                            id="infoStatus"
                            size="medium"
                            className={statusClassName}
                            type="info"
                            message={billingStatus}
                        />
                    </div>
                    <div className={styles.riSubTitle}>
                        Policy #: &nbsp;&nbsp;
                        <span>
                            <Link to={`/ri-policies/${policyNumber}/summary`} className={styles.riLinkDetails}>
                                {policyNumber}
                            </Link>
                        </span>
                        <span className={styles.riEffDateGroup}>
                            (
                            <FormattedDateRange
                                id="effectiveDate_1"
                                value={activePeriod}
                                startDate={new Date(activePeriod.effectiveDate)}
                                endDate={new Date(activePeriod.expirationDate)}
                                hideLabel
                                displayFormat="short"
                            />
                            )
                        </span>
                        <InfoLabel
                            id="infoStatus"
                            size="medium"
                            className={styles.gwInfoStatus}
                            type="info"
                            message={policyStatus}
                        />
                    </div>
                </div>
                <div className={styles.riTypeWithLOB}>
                    <div>
                        <Icon icon={LobIconUtil.getMaterialIcon(productName)} className={styles.syncIcon} />
                    </div>
                    <div>
                        <h2>{policyHolderName}</h2>
                    </div>
                </div>
            </div>
        );
    };

    isClaimsTurnedOn = () => {
        const { authUserData } = this.props;
        return isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'claim' }) && (hasPermission_RI({ authUserData, permissionName: 'hasClaimsAccess' }));
    };

    hasBillingAccess = () => {
        const { authUserData } = this.props;
        return isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'billing' }) && (hasPermission_RI({ authUserData, permissionName: 'hasAccountingAccess' }));
    }

    handleTilesOnClick = (id) => {
        this.setState({ currentView: id });
    };

    render() {
        const {
            policyResponse,
            currentView,
            isLoading,
            billingIcon,
            policyBillingData
        } = this.state;
        const productName = _.get(policyResponse, 'product.productCode');
        if (isLoading) {
            return <Loader loaded={!isLoading} />;
        }

        if (_.isEmpty(policyResponse)) {
            return null;
        }

        const overrides = {
            [currentView]: {
                active: true
            },
            billing: {
                active: currentView === 'billing',
                tileIcon: billingIcon,
                visible: this.hasBillingAccess()
            },
            claims: {
                active: currentView === 'claims',
                visible: this.isClaimsTurnedOn()
            },
            policyProductName: {
                content: this.getAccountHolderNameandNumber(policyResponse, '')
            },
            tabPolicyTitle: {
                content: this.getPolicyHeader()
            }
        };

        const resolvers = {
            resolveComponentMap: {
                // accountheadercomponent: FormattedHeaderComponent
            },
            resolveCallbackMap: {
                handleTilesOnClick: this.handleTilesOnClick,
                handlePolicNumberOnClick: this.handlePolicNumberOnClick
            },
            resolveClassNameMap: styles
        };
        const {
            match: { url }
        } = this.props;

        const policyDetailsData = {
            policyResponse: policyResponse,
            getUpdateCountInfo: this.updateCountInfo,
        };
        return (
            <DependencyProvider value={{ DocumentPageService }}>
                <ViewModelForm
                    uiProps={metadata.pageContent}
                    model={policyResponse}
                    overrideProps={overrides}
                    callbackMap={resolvers.resolveCallbackMap}
                    componentMap={resolvers.resolveComponentMap}
                    classNameMap={resolvers.resolveClassNameMap}
                />
                <CommonRoutes
                    steps={this.getRoutesWithFeature()}
                    basePath={url}
                    setDocumentCount={this.updateDocumentCount}
                    policyNumber={policyResponse.policyNumber}
                    policyDetailsData={policyDetailsData}
                    billingData={policyBillingData}
                />
            </DependencyProvider>
        );
    }
}
export default withRouter(withAuthenticationContext(RIPolicyDetails));
